'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { formatUnits, parseUnits } from 'viem';

import { formatNumberWithCommasAndDecimals } from '@/lib/utils/formatNumberWithCommasAndDecimals';
import { formatPriceInput } from '@/lib/utils/formatPriceInput';
import { removeCommas } from '@/lib/utils/removeCommas';
import { C_Regexp } from '@/ts/constants/C_Regexp';
import { E_LastEdited } from '@/ts/enums/E_LastEdited';

// Helper: Check if input is a valid numeric string (allows "", ".", "0.", commas, etc.)
export function isNumericInput(value: string): boolean {
  if (value === '' || value === '.') return true;
  // Remove commas for validation
  const valueWithoutCommas = removeCommas(value);
  // Match: optional sign, digits, optional decimal point, optional digits
  // Also allows leading zeros like "0.", "0.0"
  return C_Regexp.numericInput.test(valueWithoutCommas);
}

// Helper: Clamp decimal places in a string to prevent parseUnits errors
export function clampDecimals(value: string, maxDecimals: number): string {
  if (!value || value === '.' || value === '-') return value;

  const parts = value.split('.');
  if (parts.length === 1) return value; // No decimal point

  const integerPart = parts[0];
  const decimalPart = parts[1];

  if (!decimalPart) return value; // Just a decimal point

  // Clamp decimal part to maxDecimals
  const clampedDecimal = decimalPart.slice(0, maxDecimals);
  return `${integerPart}.${clampedDecimal}`;
}

interface I_UseSyncedTokenInputsOptions {
  tokenDecimals: number; // Contract decimals (for formatUnits calculations)
  tokenDisplayDecimals: number; // Pool data decimals (for parsing/clamping/display)
  usdcDecimals?: number;
  getUsdcByTokens: (tokenAmount: bigint) => Promise<bigint>;
  getTokensByUsdc: (usdcAmount: bigint) => Promise<bigint>;
  debounceMs?: number;
}

interface I_UseSyncedTokenInputsReturn {
  tokenStr: string;
  usdcStr: string;
  onTokenChange: (value: string) => void;
  onUsdcChange: (value: string) => void;
  onTokenBlur: () => void;
  onUsdcBlur: () => void;
  lastEdited: E_LastEdited | null;
  isConverting: boolean;
  error: string | null;
}

export function useSyncedTokenInputs({
  tokenDecimals,
  tokenDisplayDecimals,
  usdcDecimals = 6,
  getUsdcByTokens,
  getTokensByUsdc,
  debounceMs = 10
}: I_UseSyncedTokenInputsOptions): I_UseSyncedTokenInputsReturn {
  const [tokenStr, setTokenStr] = useState<string>('');
  const [usdcStr, setUsdcStr] = useState<string>('');
  const [lastEdited, setLastEdited] = useState<E_LastEdited | null>(null);
  const [isConverting, setIsConverting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const requestIdRef = useRef<number>(0);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastConvertedRef = useRef<{
    field: E_LastEdited | null;
    value: string;
  }>({ field: null, value: '' });
  const isUpdatingFromCalculationRef = useRef<boolean>(false);

  // Memoize conversion functions to prevent unnecessary re-renders
  const getUsdcByTokensRef = useRef(getUsdcByTokens);
  const getTokensByUsdcRef = useRef(getTokensByUsdc);

  useEffect(() => {
    getUsdcByTokensRef.current = getUsdcByTokens;
    getTokensByUsdcRef.current = getTokensByUsdc;
  }, [getUsdcByTokens, getTokensByUsdc]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isUpdatingFromCalculationRef.current) {
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (!lastEdited) return;

    const isTokenEdited = lastEdited === E_LastEdited.TOKEN;
    const sourceValueRaw = isTokenEdited ? tokenStr : usdcStr;
    const sourceValue = removeCommas(sourceValueRaw);
    const sourceDisplayDecimals = isTokenEdited
      ? tokenDisplayDecimals
      : usdcDecimals;
    const sourceCalculationDecimals = isTokenEdited
      ? tokenDecimals
      : usdcDecimals;
    if (
      lastConvertedRef.current.field === lastEdited &&
      sourceValue === lastConvertedRef.current.value
    ) {
      return;
    }

    if (!sourceValue || sourceValue === '.' || !isNumericInput(sourceValue)) {
      setError(null);
      setIsConverting(false);
      return;
    }

    const clampedValue = clampDecimals(sourceValue, sourceDisplayDecimals);

    lastConvertedRef.current = {
      field: lastEdited,
      value: sourceValue
    };

    debounceTimerRef.current = setTimeout(async () => {
      const currentRequestId = ++requestIdRef.current;
      setIsConverting(true);
      setError(null);

      try {
        let sourceBigInt: bigint;
        try {
          const parsedWithDisplay = parseUnits(
            clampedValue,
            sourceDisplayDecimals
          );

          if (
            sourceDisplayDecimals !== sourceCalculationDecimals &&
            isTokenEdited
          ) {
            // Convert from display decimals to contract decimals
            const decimalsDiff =
              sourceCalculationDecimals - sourceDisplayDecimals;
            if (decimalsDiff > 0) {
              sourceBigInt = parsedWithDisplay * BigInt(10 ** decimalsDiff);
            } else {
              sourceBigInt =
                parsedWithDisplay / BigInt(10 ** Math.abs(decimalsDiff));
            }
          } else {
            sourceBigInt = parsedWithDisplay;
          }
        } catch (_parseError) {
          setError('Invalid number format');
          setIsConverting(false);
          return;
        }

        if (sourceBigInt === 0n) {
          if (isTokenEdited) {
            setUsdcStr('');
          } else {
            setTokenStr('');
          }
          setIsConverting(false);
          setError(null);
          return;
        }

        let resultBigInt: bigint;
        try {
          if (isTokenEdited) {
            resultBigInt = await getUsdcByTokensRef.current(sourceBigInt);
          } else {
            resultBigInt = await getTokensByUsdcRef.current(sourceBigInt);
          }
        } catch (_callError) {
          setError('Conversion failed');
          setIsConverting(false);
          return;
        }

        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        const targetCalculationDecimals = isTokenEdited
          ? usdcDecimals
          : tokenDecimals;
        const formatted = formatUnits(resultBigInt, targetCalculationDecimals);

        let prettified: string;
        if (!isTokenEdited) {
          const numValue = parseFloat(formatted);
          if (isNaN(numValue)) {
            prettified = formatted;
          } else {
            prettified = formatNumberWithCommasAndDecimals(
              numValue,
              tokenDisplayDecimals
            );
          }
        } else {
          const numValue = parseFloat(formatted);
          if (isNaN(numValue)) {
            prettified = formatted;
          } else {
            // Format with commas and exact number of USDC decimals (6)
            prettified = formatNumberWithCommasAndDecimals(
              numValue,
              usdcDecimals
            );
          }
        }

        isUpdatingFromCalculationRef.current = true;
        if (isTokenEdited) {
          setUsdcStr(prettified);
        } else {
          setTokenStr(prettified);
        }

        setIsConverting(false);
        setError(null);

        setTimeout(() => {
          isUpdatingFromCalculationRef.current = false;
        }, 50);
      } catch (_err) {
        // Check if this request is still current
        if (currentRequestId !== requestIdRef.current) {
          // Stale request, ignore
          return;
        }

        setError('An error occurred during conversion');
        setIsConverting(false);
      }
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [
    lastEdited,
    tokenDecimals,
    tokenDisplayDecimals,
    usdcDecimals,
    debounceMs,
    tokenStr,
    usdcStr
  ]);
  const onTokenChange = useCallback(
    (value: string) => {
      // Use formatPriceInput for smart formatting
      const formatted = formatPriceInput(value, tokenDisplayDecimals);

      // Validate simple numeric constraints if needed (max value check is already in formatPriceInput)
      // We can also double check here if we want to be extra safe, or rely on the util.
      // formatPriceInput handles: invalid chars, max value (999M), decimals.
      // So we just set the state.

      setTokenStr(formatted);
      setLastEdited(E_LastEdited.TOKEN);
      setError(null);
    },
    [tokenDisplayDecimals]
  );

  const onUsdcChange = useCallback(
    (value: string) => {
      // Use formatPriceInput for smart formatting
      const formatted = formatPriceInput(value, usdcDecimals);

      setUsdcStr(formatted);
      setLastEdited(E_LastEdited.USDC);
      setError(null);
    },
    [usdcDecimals]
  );

  const onTokenBlur = useCallback(() => {
    // Do NOT reformat on blur, preserving user's exact input
  }, []);

  const onUsdcBlur = useCallback(() => {
    // Do NOT reformat on blur, preserving user's exact input
  }, []);

  return {
    tokenStr,
    usdcStr,
    onTokenChange,
    onUsdcChange,
    onTokenBlur,
    onUsdcBlur,
    lastEdited,
    isConverting,
    error
  };
}
